import useChartDateIntervals, { ChartOptionDate } from '@app/hooks/use-chart-date-intervals';
import useTranslation from '@app/hooks/use-translation';
import { ChartsLayoutType, DashboardsSettingsType } from '@app/interfaces/dashboards.type';
import { ReportChart, NumberChartResponse } from '@app/interfaces/report.type';

import {
  useLazyGetGraphDataQuery,
  useLazyGetChatGraphDataQuery,
} from '@app/store/api/graph-data.api';

import { editSelectedChartRangeDashboard } from '@app/store/reducers/dashboard-charts.slice';
import { editSelectedReportChartRange } from '@app/store/reducers/report-charts.slice';
import { useAppDispatch } from '@app/store/store';

import { TooltipWrapper, Divide, DropMenu, CircleButton } from '@ui';
import React, { FC, useCallback, useMemo } from 'react';

import { usePermissionCheck } from '@app/hooks/use-permission';
import { userPermissions } from '@app/utils/user-permissions';
import { FilterItem } from '@app/interfaces/filter';
import moment from 'moment';
import { RecordRangeRangeParametersItemForRType } from '@app/interfaces/record';

import { DropMenuItem } from '../ui/drop-menu/drop-menu.type';

const ChartHeader: FC<{
  visibleToolbarState: boolean;
  chartWidth: number;
  chart_widget_info?: { widget_id: string; chart_widget_id: string };
  data: ReportChart | NumberChartResponse;
  chartHeight: number;
  widgetMode: boolean;
  viewOnly: boolean;
  dashboard: boolean;
  handleEditChart: ({ key }: { key: string }) => void;
  currentTemplate: 'chat' | 'chats' | 'voice';
  setOpenedCustomDateModal: React.Dispatch<React.SetStateAction<boolean>> | undefined;
  layoutSettings: ChartsLayoutType[];
  handleChangeWidgetChartInterval?(
    interval: string,
    widget_info: { widget_id: string; chart_widget_id: string } | undefined,
  ): void;
  currentDashboard: DashboardsSettingsType | undefined;
  setData:
    | React.Dispatch<React.SetStateAction<ReportChart | NumberChartResponse | undefined>>
    | undefined;
  dashboard_graph_id: string | undefined;
  filterValue: FilterItem[] | undefined;
}> = ({
  chartHeight,
  chartWidth,
  visibleToolbarState,
  handleChangeWidgetChartInterval,
  widgetMode,
  data,
  viewOnly,
  currentTemplate,
  layoutSettings,
  setOpenedCustomDateModal,
  handleEditChart,
  dashboard,
  chart_widget_info,
  dashboard_graph_id,
  setData,
  currentDashboard,
  filterValue,
}) => {
  const { t } = useTranslation('pages.chartPage');
  const dispatch = useAppDispatch();

  const [getVoiceGraphData] = useLazyGetGraphDataQuery();
  const [getChatGraphData] = useLazyGetChatGraphDataQuery();

  const getGraphData = useMemo(
    () => ({ chats: getChatGraphData, voice: getVoiceGraphData }),
    [getChatGraphData, getVoiceGraphData],
  );

  const { daysSelectOptions, dateIntervals, dateToString, dateKeys } = useChartDateIntervals();

  // MAP → O(1) вместо .find() в каждом case
  const rangeTitleMap = useMemo(() => {
    return daysSelectOptions.reduce((acc, item) => {
      acc[item.key] = item.title;
      return acc;
    }, {} as Record<string, string>);
  }, [daysSelectOptions]);

  const range = useMemo(() => {
    return (data.range?.parameters ?? [])
      .slice()
      .sort((a, b) => b.condition - a.condition) as RecordRangeRangeParametersItemForRType[];
  }, [data.range?.parameters]);

  const chartCanBeEdited = usePermissionCheck({
    tag:
      currentTemplate === 'chats' || currentTemplate === 'chat'
        ? userPermissions.action.actionEditChatLiveReportGraph
        : userPermissions.action.actionEditLiveReportGraph,
    permissions: 'action',
  });

  // const chartSeriesLength = useMemo(() => {
  //   if (data.type === 'number') return 1;
  //   return data?.series?.reduce((acc, item) => acc + (item.data?.length ?? 0), 0) ?? 0;
  // }, [data]);

  const disableChartActions = useMemo(() => {
    if (!dashboard) {
      if (!chartCanBeEdited) {
        return dashboard && !currentDashboard?.owner;
      }
      return true;
    }

    return dashboard && !currentDashboard?.owner;
  }, [chartCanBeEdited, currentDashboard?.owner, dashboard]);

  const editChartOptions: Array<DropMenuItem> = useMemo(
    () => [
      {
        title: widgetMode ? t('drop_menu.edit_period') : t('drop_menu.edit'),
        icon: 'EditIcon',
        key: 'edit',
        disable: disableChartActions,
      },
      {
        title: t('drop_menu.to_report'),
        icon: 'MetricIcon',
        key: 'to_report',
        disable: !!data.errorStatus,
      },
      // {
      //   content: (
      //     <div className="flex items-center gap-[5px]">
      //       <div>{t('drop_menu.toolbar')}</div>
      //       {visibleToolbarState && <CheckIcon className="text-action" size={12} />}
      //     </div>
      //   ),
      //   icon: 'ChartIcon',
      //   key: 'toolbar',
      //   disable: !chartSeriesLength,
      // },
      {
        title: t('drop_menu.remove'),
        color: 'danger',
        icon: 'TrashIcon',
        key: 'remove',
        disable: disableChartActions,
      },
    ],
    [widgetMode, t, disableChartActions, data.errorStatus],
  );
  const handlerChangeChartDateSelect = useCallback(
    (dateInterval: string) => {
      const isCustom = dateKeys.customDate === dateInterval;

      if (isCustom) {
        setData?.(data);
        setOpenedCustomDateModal?.(true);
        return;
      }

      const request = getGraphData[currentTemplate];

      request({
        id: data.graph_id,
        settings: {
          range: dateIntervals[dateInterval],
          ...(dashboard ? {} : { filter: filterValue }),
        },
      }).then((res) => {
        if (!res?.data) return;

        const chartData = (res.data as any).data as ReportChart;

        const layout = layoutSettings.find((l) =>
          dashboard ? l.i === dashboard_graph_id : l.i === chartData.graph_id,
        );

        if (dashboard) {
          dispatch(
            editSelectedChartRangeDashboard({
              layout,
              chartData,
              id: dashboard_graph_id || '',
            }),
          );
        } else {
          dispatch(
            editSelectedReportChartRange({
              layout,
              chartData,
              id: chartData.graph_id,
            }),
          );
        }
      });
    },
    [
      dateKeys.customDate,
      getGraphData,
      currentTemplate,
      data,
      dateIntervals,
      dashboard,
      filterValue,
      layoutSettings,
      dashboard_graph_id,
      dispatch,
      setData,
      setOpenedCustomDateModal,
    ],
  );

  const currentRange = dateToString(data?.range) as ChartOptionDate;

  return (
    <div className="flex items-start justify-between pl-[20px] w-full">
      <div className="flex flex-col w-full">
        <div className="flex items-center justify-between w-full">
          {!widgetMode ? (
            <div
              style={{
                maxWidth: chartWidth - (visibleToolbarState ? 210 : 100),
              }}
              className="truncate"
            >
              <TooltipWrapper content={data?.name} id={data?.name + data.graph_id}>
                <h2 className="truncate text-1color font-[700]" style={{ fontSize: 18 }}>
                  {data?.name}
                </h2>
              </TooltipWrapper>
            </div>
          ) : (
            <div className="h-full w-full">
              <DropMenu
                dropHeight={180}
                // dropHeight={(chartHeight || 0) > 350 ? 280 : Math.max((chartHeight || 0) - 100, 55)}
                dropSize={210}
                onClick={(key) => handleChangeWidgetChartInterval?.(key, chart_widget_info)}
                menu={daysSelectOptions.map((item) => ({
                  ...item,
                  isActive: currentRange === item.key,
                }))}
                position="start"
              >
                <div className="noDrag text-4color font-[400]  cursor-pointer text-[12px]">
                  <RangeMarkup currentRange={currentRange} range={range} titleMap={rangeTitleMap} />
                </div>
              </DropMenu>
            </div>
          )}

          {(chartWidth || 0) > 120 && (
            <div className="flex items-center  relative">
              {!viewOnly && (
                <div className="flex items-center noDrag mr-[15px]">
                  {!widgetMode && (
                    <Divide
                      size={15}
                      span={1}
                      orientation="vertical"
                      className="ml-[16px] mr-[5px]"
                    />
                  )}
                  <DropMenu
                    dropSize={210}
                    onClick={(key) => handleEditChart({ key })}
                    hintTitle={t('menu')}
                    menu={
                      !widgetMode
                        ? editChartOptions.filter((i) => i.key !== 'to_report')
                        : editChartOptions.filter((_, i) => i !== editChartOptions.length - 1)
                    }
                  >
                    <CircleButton icon="VerticalDotsIcon" size={12} className="text-3color" />
                  </DropMenu>
                </div>
              )}
            </div>
          )}
        </div>

        {!widgetMode && data && (chartWidth || 0) > 230 && (
          <div className="h-full w-full">
            <DropMenu
              dropHeight={(chartHeight || 0) > 350 ? 280 : Math.max((chartHeight || 0) - 100, 55)}
              dropSize={210}
              onClick={handlerChangeChartDateSelect}
              menu={daysSelectOptions.map((item) => ({
                ...item,
                isActive: currentRange === item.key,
              }))}
              position="start"
            >
              <div className="noDrag text-4color font-[400] hover:underline cursor-pointer text-[12px]">
                <RangeMarkup currentRange={currentRange} range={range} titleMap={rangeTitleMap} />
              </div>
            </DropMenu>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChartHeader;

const RangeMarkup: FC<{
  currentRange: ChartOptionDate;
  range: RecordRangeRangeParametersItemForRType[];
  titleMap: Record<string, string>;
}> = ({ currentRange, range, titleMap }) => {
  const { t } = useTranslation('pages.chartPage');

  if (currentRange === 'customDate') {
    return (
      <div className="flex items-center gap-[10px]">
        {range.map((r, i) => (
          <div key={i}>
            {r.condition === '>' ? t('interval_under_label.from') : t('interval_under_label.to')}
            &nbsp;
            {moment(r.value).format('DD.MM.YYYY  HH:mm')}
          </div>
        ))}
      </div>
    );
  }

  return <div>{titleMap[currentRange]}</div>;
};
